import jsPDF from 'jspdf'
import 'jspdf-autotable'
import domtoimage from 'dom-to-image'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import html2canvas from 'html2canvas-pro'
import countries from './countries'
import { addSeconds } from 'date-fns'
import { parse } from 'date-fns'

const institution = import.meta.env.VITE_INSTITUATION

export const country = countries[import.meta.env.VITE_INSTITUATION]

export const setToLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value))
}

export const getItemLocalStorage = (key) =>
  localStorage.getItem(key) !== 'undefined'
    ? JSON.parse(localStorage.getItem(key))
    : null

export function generateRandomString(length = 7, chars) {
  let str = ''
  if (!chars) {
    chars = '1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  }
  chars = chars.split('')

  for (let i = 0; i < length; i++) {
    str += chars[Math.floor(Math.random() * chars.length)]
  }

  return str
}

export function hasAccess(meta, accountData) {
  const { permissions, user_type, cust_type, institutions } = meta
  // Helper function to check if the user has access
  const hasPermissions =
    permissions?.includes('*') ||
    permissions?.some((item) =>
      accountData?.MENULIST.find(({ MENUID }) => MENUID == item),
    )

  const isUserType =
    user_type?.includes('*') || user_type?.includes(accountData?.USERTYPE)

  const isCustType =
    cust_type?.includes('*') || cust_type?.includes(accountData?.CUST_TYPE)

  const correctInst =
    institutions?.includes('*') || institutions?.includes(country.id)

  return hasPermissions && isUserType && isCustType && !!correctInst
}

export const formatAmount = (amount) => {
  // save the original value
  const original_amount = amount
  // Check if the amount is a string and try to convert it to a number
  if (typeof amount === 'string') {
    amount = parseFloat(amount.replace(/[^0-9.]/g, ''))
  }

  // Check if the amount is a valid number
  if (isNaN(amount)) {
    return original_amount
  }

  // Format the amount
  return `${country.currency} ${new Intl.NumberFormat(country.code || 'en-KE').format(amount)}`
}

export function generateTimestamps({ date, time }) {
  // Reformat the date to 'YYYY-MM-DD'
  const [day, month, year] = date.split('-')
  const formattedDate = `${year}-${month}-${day}`

  // Combine date and time
  const dateTimeString = `${formattedDate}T${time}`

  // Create a new Date object
  const timestamp = new Date(dateTimeString)

  // Get the timestamp in milliseconds
  const milliseconds = timestamp.getTime()
  return milliseconds
}

// Function to generate random color for each line
function getRandomColor() {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

export function serializeMinistatement(data) {
  // group by RRN
  let data_arr = data.reduce((acc, item) => {
    const idx = acc.findIndex((ac) => ac.RRN == item.RRN)
    let obj = {}
    if (idx < 0) {
      obj = {
        ...item,
        total: Number(item.ENTRY_AMOUNT),
        datetime: generateTimestamps({
          date: item.TRAN_DATE,
          time: item.TRAN_TIME,
        }),
      }
      acc.push(obj)
    } else {
      let trx = acc[idx]
      obj = {
        ...trx,
        total: trx.total + Number(item.ENTRY_AMOUNT),
      }
      acc[idx] = obj
    }
    return acc
  }, [])

  return data_arr
}

export function serializeChartData(data = []) {
  // extract labels;
  const groupedData = {}
  let stamps = new Set()
  data.forEach((transaction) => {
    const cardNum = transaction.MASKED_CARD
    if (!groupedData[cardNum]) {
      groupedData[cardNum] = []
    }
    groupedData[cardNum].push({
      date: transaction.datetime,
      amount: parseFloat(transaction.total).toFixed(2),
    })
    stamps.add(transaction.datetime)
  })
  stamps = [...stamps].sort((a, b) => a - b)

  const datasets = Object.keys(groupedData).map((cardNum) => {
    const dt = groupedData[cardNum].map((i) => {
      return { x: i.date, y: Number(i.amount) }
    })
    return {
      label: cardNum,
      name: cardNum,
      data: dt,
      fill: false,
      borderColor: getRandomColor(),
    }
  })
  return { datasets }
}

export function serializeDealerChartData(data = [], dates = []) {
  // extract labels;
  const groupedData = {}
  data.forEach((transaction) => {
    const product = transaction.PRODUCT
    if (!groupedData[product]) {
      groupedData[product] = []
    }
    groupedData[product].push({
      date: transaction.datetime,
      amount: parseFloat(transaction.total).toFixed(2),
    })
  })

  const datasets = Object.keys(groupedData).map((product) => {
    const dt = groupedData[product].map((i) => {
      return { x: i.date, y: i.amount }
    })
    return {
      name: product.toLocaleLowerCase() === 'na' ? 'OTHERS' : product,
      data: dt,
      fill: false,
      borderColor: getRandomColor(),
    }
  })
  return { datasets, labels: dates }
}

export function serializeDealerMinistatement(data) {
  // group by RRN
  let data_arr = data.reduce((acc, item) => {
    const idx = acc.findIndex((ac) => ac.RRN == item.RRN)
    let obj = {}
    if (idx < 0) {
      obj = {
        ...item,
        total: Number(item.TXNAMOUNT),
        datetime: generateDealerTimestamps({
          date: item.TXNDATE.split(' ')[0],
          time: item.TXNTIME,
        }),
      }
      acc.push(obj)
    } else {
      let trx = acc[idx]
      obj = {
        ...trx,
        total: trx.total + Number(item.TXNAMOUNT),
      }
      acc[idx] = obj
    }
    return acc
  }, [])

  return data_arr
}

export function generateDealerTimestamps({ date, time }) {
  // Reformat the date to 'YYYY-MM-DD'
  const [year, month, day] = date.split('-')
  const formattedDate = `${year}-${month}-${day}`

  // Combine date and time
  const dateTimeString = `${formattedDate}T${time}`

  // Create a new Date object
  const timestamp = new Date(dateTimeString)

  // Get the timestamp in milliseconds
  const milliseconds = timestamp.getTime()
  return milliseconds
}

export const getCardStrings = (cards) => {
  const cardsString = cards.reduce(
    (acc, card) =>
      `${acc === '' ? acc : `${acc},`}'${card?.id || card?.ENCCARDNUMBER || card}'`,
    '',
  )
  return cardsString
}

export function formatTimestamp(milliseconds) {
  const date = new Date(milliseconds)
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false, // Use 24-hour format
  }
  return date.toLocaleString('en-US', options)
}

// Reports utility functions
export function downloadPdfReport(base64String, filename) {
  // Create a link element
  const link = document.createElement('a')

  // Set the title of the link
  link.title = filename

  // Set the href to the base64 string
  link.href = `data:application/pdf;base64,${base64String}`

  // Set the download attribute to the filename
  link.download = filename //getting filename from response

  // Append the link to the body
  document.body.appendChild(link)

  // Programmatically click the link to trigger the download
  link.click()

  // Remove the link from the body
  document.body.removeChild(link)
}

export async function downloadPdf(fileName) {
  try {
    const headerEl = document.getElementById('table-header')
    const dateHeaderEl = document.getElementById('date-header')
    const padding = 10
    let startY = 0

    // Temporarily apply inline styles to the header element
    const originalHeaderStyle = headerEl.style.cssText
    headerEl.style.color = '#000000'
    headerEl.style.fontSize = '24px'

    // Capture the header
    const headerCanvas = await html2canvas(headerEl, {
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      scale: 3,
      width: '1000',
      height: '40', // Set width for higher resolution
    })
    const headerImgData = headerCanvas.toDataURL('image/png')
    const headerWidth = 700
    const headerHeight = 40
    startY += headerHeight + padding

    // Restore the original styles
    headerEl.style.cssText = originalHeaderStyle

    // Create a new PDF document with a larger format
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: 'a3', // Use a3 format for more space
    })

    // Add the header image to the PDF
    pdf.addImage(
      headerImgData,
      'PNG',
      padding,
      padding,
      headerWidth,
      headerHeight,
    )

    if (dateHeaderEl) {
      // Temporarily apply inline styles to the header element
      const originalDateStyle = dateHeaderEl.style.cssText
      dateHeaderEl.style.color = '#000000'
      dateHeaderEl.style.fontSize = '8px'
      dateHeaderEl.style.fontWeight = '600'
      const dateCanvas = await html2canvas(dateHeaderEl, {
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        scale: 3,
        width: '1000',
        height: '40', // Set width for higher resolution
      })
      const dateImgData = dateCanvas.toDataURL('image/png', 1)
      const dateWidth = 1000
      const dateHeight = 40

      // Add the header image to the PDF
      pdf.addImage(dateImgData, 'PNG', padding, startY, dateWidth, dateHeight)
      startY += dateHeight + padding

      // Restore the original styles
      dateHeaderEl.style.cssText = originalDateStyle
    }

    // Pre-process table to fix number formatting before generating PDF
    const tableElement = document.getElementById('table')
    if (tableElement) {
      // Process all table cells that might contain currency amounts
      const currencyCells = tableElement.querySelectorAll('td')
      currencyCells.forEach((cell) => {
        const text = cell.textContent.trim()

        // Get the cell's column index
        const cellIndex = cell.cellIndex
        // Get the corresponding header cell
        const headerCell = cell
          .closest('table')
          .querySelector(`th:nth-child(${cellIndex + 1})`)
        const headerText = headerCell?.textContent?.toLowerCase() || ''

        // Check if this specific column should contain currency
        const isCurrencyColumn =
          headerText.includes('amount') ||
          headerText.includes('balance') ||
          headerText.includes('limit') ||
          text.startsWith(country.currency)

        if (isCurrencyColumn) {
          // Extract numeric value, removing currency symbol and non-numeric chars except decimal point
          let numericValue = text.replace(country.currency, '').trim()
          numericValue = numericValue.replace(/[^\d.]/g, '')

          try {
            const num = parseFloat(numericValue)
            if (!isNaN(num)) {
              // Store original value in a data attribute
              cell.setAttribute('data-original-value', text)
              // Replace with simplified format that won't break in PDF
              cell.textContent =
                country.currency + ' ' + num.toLocaleString('en-US')
            }
          } catch (e) {
            // If parsing fails, leave original text
          }
        }
      })
    }

    // Use autoTable to add the table content with adjusted settings
    pdf.autoTable({
      html: '#table',
      startY,
      margin: { top: padding, left: padding, right: padding, bottom: padding },
      theme: 'grid',
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      styles: {
        fontSize: 10,
        cellPadding: 3,
        overflow: 'linebreak',
        lineWidth: 0.1,
        lineColor: [80, 80, 80],
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
      },
      didParseCell: function (data) {
        // For numeric cells, ensure they're right-aligned
        if (data.section === 'body') {
          const text = data.cell.text
          if (typeof text === 'string') {
            // Check if this is a currency/number cell
            if (text.includes(country.currency) || /[\d]+[,\.]\d+/.test(text)) {
              data.cell.styles.halign = 'right'
              data.cell.styles.font = 'helvetica'
              data.cell.styles.overflow = 'linebreak'
              data.cell.styles.fontStyle = 'normal'
              // Ensure the text doesn't contain any problematic characters
              if (
                typeof data.cell.text === 'string' &&
                data.cell.text.includes('/')
              ) {
                // Replace any slashes that might appear in the text
                data.cell.text = data.cell.text.replace(/\//g, '')
              }
            }
          }
        }
      },
      // Customize cell drawing
      didDrawCell: function (data) {
        // If we need additional cell drawing customization
      },
      // Set table width to prevent overflow
      tableWidth: 'auto',
    })

    // Restore original table cell values
    if (tableElement) {
      const processedCells = tableElement.querySelectorAll(
        'td[data-original-value]',
      )
      processedCells.forEach((cell) => {
        cell.textContent = cell.getAttribute('data-original-value')
        cell.removeAttribute('data-original-value')
      })
    }

    // Generate the filename
    const date = new Date()
    const filename = `${fileName}_${date.getFullYear()}${`0${date.getMonth() + 1}`.slice(-2)}${`0${date.getDate()}`.slice(-2)}${`0${date.getHours()}`.slice(-2)}${`0${date.getMinutes()}`.slice(-2)}${`0${date.getSeconds()}`.slice(-2)}.pdf`

    // Save the PDF
    pdf.save(filename)
    return 0
  } catch (error) {
    return 1
  }
}

export function downloadXSheetReport(base64, filename) {
  // Decode the base64 string to binary string
  const binaryString = window.atob(base64)
  // Create an 8-bit unsigned array from the binary string
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  // Create a Blob object from the bytes
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  // Create an anchor element and trigger a download
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  const date = new Date()
  const fileName = `${
    filename
  }_${date.getFullYear()}${`0${date.getMonth() + 1}`.slice(
    -2,
  )}${`0${date.getDate()}`.slice(-2)}${`0${date.getHours()}`.slice(
    -2,
  )}${`0${date.getMinutes()}`.slice(-2)}${`0${date.getSeconds()}`.slice(-2)}`
  link.download = fileName
  link.click()
}

export async function downloadSheetReportFromJson(data, title, name) {
  try {
    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet([])

    // Add title to the first row
    XLSX.utils.sheet_add_aoa(worksheet, [[title]], { origin: 'A1' })

    // Merge cells for the title
    if (!worksheet['!merges']) worksheet['!merges'] = []
    worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } })

    // Apply bold formatting to the title
    if (!worksheet.A1) worksheet.A1 = {}
    worksheet.A1.s = {
      font: {
        bold: true,
        sz: 14,
      },
      alignment: {
        horizontal: 'center',
      },
    }

    // Add data starting from the second row
    XLSX.utils.sheet_add_json(worksheet, data, {
      origin: 'A2',
      skipHeader: false,
    })

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    // Convert the workbook to a binary string
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' })

    // Convert binary string to array buffer
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length)
      const view = new Uint8Array(buf)
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff
      return buf
    }

    // Save the workbook to a file
    const date = new Date()
    const filename = `${name}_${date.getFullYear()}${`0${date.getMonth() + 1}`.slice(
      -2,
    )}${`0${date.getDate()}`.slice(-2)}${`0${date.getHours()}`.slice(
      -2,
    )}${`0${date.getMinutes()}`.slice(-2)}${`0${date.getSeconds()}`.slice(
      -2,
    )}.xlsx`

    saveAs(
      new Blob([s2ab(wbout)], { type: 'application/octet-stream' }),
      filename,
    )

    // Add a logo to the workbook
    const img = new Image()
    img.src = 'assets/media/logos/rubis-logo-white.png'
    img.onload = async () => {
      const canvas = await html2canvas(img)
      const imgData = canvas.toDataURL('image/png')
      // Add the image data to the workbook
      // Note: This part requires additional handling to embed the image in the XLSX file
    }
    return 0
  } catch (error) {
    console.error('oops, something went wrong!', error)
    return 1
  }
}

export function downloadSheetFromJson(data, name = 'sample') {
  try {
    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data)

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
    const date = new Date()
    const filename = `${name}_${date.getFullYear()}${`0${date.getMonth() + 1}`.slice(
      -2,
    )}${`0${date.getDate()}`.slice(-2)}${`0${date.getHours()}`.slice(
      -2,
    )}${`0${date.getMinutes()}`.slice(-2)}${`0${date.getSeconds()}`.slice(
      -2,
    )}.xlsx`

    // Write the workbook to a file
    XLSX.writeFile(workbook, filename)
  } catch (error) {
    console.error('oops, something went wrong!', error)
  }
}

export function handleXlsxUpload(event, callback) {
  try {
    const file = event.target.files[0]
    const reader = new FileReader()

    reader.onload = function (e) {
      const data = e.target.result
      const workbook = XLSX.read(data, { type: 'binary' })

      workbook.SheetNames.forEach((sheetName) => {
        // Convert the sheet data to JSON
        const XL_row_object = XLSX.utils.sheet_to_row_object_array(
          workbook.Sheets[sheetName],
        )
        callback(XL_row_object)
      })
    }

    reader.onerror = function (ex) {}

    reader.readAsBinaryString(file)
  } catch (error) {
    callback([])
  }
}

export const isValidAmount = (amount) => {
  // const pattern = /^\d*$/;
  const pattern = /^\d+(\.\d{1,2})?$/

  return pattern.test(amount) ? Number(amount) > 0 : false
}

export const convertStringToSentenseCase = (str) => {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
  )
}

export const getTimeFromDate = (date) => {
  const time = new Date(date).toLocaleTimeString('en-US')

  return time
}

export const getFormattedDate = (date) => {
  const dateObj = new Date(date)
  const year = dateObj.getFullYear()
  const month = dateObj.getMonth()
  const day = dateObj.getDate()
  return `${day}/${month}/${year}`
}

export const addSecondsToDate = (date, seconds) => {
  // Create a new Date object to avoid modifying the original
  const newDate = addSeconds(new Date(date), seconds)

  return newDate
}

export const convertToDateTime = (dateString, timeString) => {
  const combinedString = `${dateString}T${timeString}`

  return new Date(combinedString).toLocaleString()
}

export const convertToTitleCase = (word) => {
  const lowerCaseWord = word.toLowerCase()
  return lowerCaseWord.charAt(0).toUpperCase() + lowerCaseWord.substr(1)
}

export const formatJourneyDateTime = (dateString, timeString) => {
  // Parse the journey date
  const journeyDate = parse(dateString, 'dd/MM/yyyy', new Date())

  // Parse the journey time
  const [time, period] = timeString.split(' ')
  const [hours, minutes, seconds] = time.split(':')

  // Create a new date object with the journey date and time
  const dateTime = new Date(journeyDate)
  dateTime.setHours(
    period === 'PM' && hours !== '12'
      ? parseInt(hours) + 12
      : period === 'AM' && hours === '12'
        ? 0
        : parseInt(hours),
    parseInt(minutes),
    parseInt(seconds),
  )

  return dateTime
}

export function getUserTimezone() {
  try {
    // Get timezone from Intl API - most reliable cross-browser method
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // Check if we got a valid timezone
    if (timezone && timezone.length > 0) {
      return timezone
    }

    // Fallback to server timezone if detection fails
    return 'Africa/Nairobi'
  } catch (error) {
    console.warn('Failed to detect user timezone:', error)
    return 'Africa/Nairobi' // Default fallback
  }
}

export function getTimeStamp() {
  const userTimezone = getUserTimezone()

  const userTime = new Date().toLocaleString('en-US', {
    timeZone: userTimezone || 'Africa/Nairobi',
  })

  // Convert the localized time to a Date object
  const userDate = new Date(userTime)

  // Format the date as yyyyMMddHHmmss
  const year = userDate.getFullYear()
  const month = String(userDate.getMonth() + 1).padStart(2, '0') // months are 0-indexed
  const day = String(userDate.getDate()).padStart(2, '0')
  const hours = String(userDate.getHours()).padStart(2, '0')
  const minutes = String(userDate.getMinutes()).padStart(2, '0')
  const seconds = String(userDate.getSeconds()).padStart(2, '0')

  // Combine into the desired format
  const formattedTimestamp = `${year}${month}${day}${hours}${minutes}${seconds}`

  return formattedTimestamp
}

export const getPdfFileName = (custName, respFileName, reportType) => {
  if (respFileName) {
    return respFileName
  }
  switch (reportType) {
    case 'CLI_INVOICE_RPT':
      return `Facture_${custName}_${getTimeStamp()}.pdf`
    default:
      return `Report_${custName}_${getTimeStamp()}.pdf`
  }
}
